import { createSelector } from 'redux-orm';

import orm from '../orm';

export const makeSelectListReportDataByListId = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    ({ ListReportData }, listId, pointSystemId) => {
      if (!listId) {
        return null;
      }

      const rows = ListReportData.all().filter({ listId }).toRefArray();
      if (rows.length === 0) {
        return null;
      }

      if (pointSystemId) {
        const matched = rows.find((row) => String(row.pointSistemId) === String(pointSystemId));
        if (matched) {
          return matched;
        }
      }

      return rows[0];
    },
  );

export const selectListReportDataByListId = makeSelectListReportDataByListId();

// Селектор для проверки условий автоматического закрытия месяца
// Возвращает true, если:
// 1. Все задачи в списке закрыты (CardPointData.closed === true)
// 2. У всех закрытых задач есть closedAt
// 3. Месяц/год closedAt соответствует выбранному месяцу/году
export const makeSelectShouldAutoCloseMonth = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    (_, __, pointSystemId) => pointSystemId,
    (_, __, ___, year) => year,
    (_, __, ___, ____, month) => month,
    ({ List, Card, CardPointData, CardPointArchivedData }, listId, pointSystemId, year, month) => {
      if (!listId || !pointSystemId || !year || !month) {
        return false;
      }

      const listModel = List.withId(listId);
      if (!listModel) {
        return false;
      }

      // Пропускаем архивные списки
      if (listModel.is_archive === true) {
        return false;
      }

      // Получаем все карточки в списке (не архивные)
      const cardModels = listModel.cards.toModelArray().filter(
        (cardModel) => cardModel.is_archive !== true
      );

      // Если нет карточек, не закрываем месяц
      if (cardModels.length === 0) {
        return false;
      }

      // Проверяем каждую карточку
      for (const cardModel of cardModels) {
        const cardPointDataModel = CardPointData.all()
          .filter({ cardId: cardModel.id, pointSistemId: pointSystemId })
          .first();

        // Если нет CardPointData или задача не закрыта, не закрываем месяц
        if (!cardPointDataModel || cardPointDataModel.closed !== true) {
          return false;
        }

        // Для определения месяца закрытия используем taskClosedAt (task_closed_at) из CardPointArchivedData.
        // Если archived данных нет (например, задача закрыта через upsert closed=true), используем fallback на CardPointData.closedAt.
        const archivedModels = CardPointArchivedData.all()
          .filter({ cpdId: cardPointDataModel.id, pointSistemId: pointSystemId })
          .toModelArray();

        const archivedLatest = archivedModels && archivedModels.length > 0
          ? archivedModels.reduce((acc, model) => {
              const a = acc?.ref?.taskClosedAt ? new Date(acc.ref.taskClosedAt).getTime() : -Infinity;
              const b = model?.ref?.taskClosedAt ? new Date(model.ref.taskClosedAt).getTime() : -Infinity;
              return b > a ? model : acc;
            }, null)
          : null;

        const taskClosedAtRaw = archivedLatest?.ref?.taskClosedAt || cardPointDataModel.closedAt;

        if (!taskClosedAtRaw) {
          return false;
        }

        // Проверяем, что месяц и год taskClosedAt соответствуют выбранным
        const taskClosedAtDate = new Date(taskClosedAtRaw);
        if (Number.isNaN(taskClosedAtDate.getTime())) {
          return false;
        }

        const taskClosedAtYear = taskClosedAtDate.getFullYear();
        const taskClosedAtMonth = taskClosedAtDate.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

        if (taskClosedAtYear !== Number(year) || taskClosedAtMonth !== Number(month)) {
          return false;
        }
      }

      return true;
    },
  );

export const selectShouldAutoCloseMonth = makeSelectShouldAutoCloseMonth();

export default {
  makeSelectListReportDataByListId,
  selectListReportDataByListId,
  makeSelectShouldAutoCloseMonth,
  selectShouldAutoCloseMonth,
};
